import {
  defaultShouldDehydrateQuery,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import SuperJSON from "superjson";

import {
  checkIsForbiddenError,
  checkIsNotFoundError,
  checkIsTrpcClientError,
  checkIsUnauthorizedError,
} from "@/common/errors";

const QUERY_STALE_TIME_IN_SECONDS = 20;
const QUERY_GARBAGE_COLLECTION_TIME_IN_DAYS = 7;

export const createQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * QUERY_STALE_TIME_IN_SECONDS,
        gcTime: 1000 * 60 * 60 * 24 * QUERY_GARBAGE_COLLECTION_TIME_IN_DAYS,
      },
      dehydrate: {
        serializeData: SuperJSON.serialize,
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) ||
          query.state.status === "pending",
      },
      hydrate: {
        deserializeData: SuperJSON.deserialize,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // 🎉 only show error toasts if we already have data in the cache
        // which indicates a failed background update
        if (query.state.data !== undefined) {
          let message = "Unknown error";
          if (checkIsUnauthorizedError(error)) {
            // If the user is unauthorized, we need to reload the page to either redirect to the login page or to the welcome page
            window.location.reload();
          }
          if (checkIsForbiddenError(error)) {
            toast.error(
              "We are currently under maintenance. Please try again later!",
              {
                id: "maintenance",
              },
            );
            return;
          }
          if (checkIsTrpcClientError(error)) {
            message = replaceProviders(error.message);
          }
          if (!checkIsNotFoundError(error)) {
            toast.error(`Something went wrong: ${message}`, {
              id: "network-error",
            });
          }
        }
      },
    }),
  });

function replaceProviders(text: string) {
  const providers = [
    "runware ai",
    "runware",
    "fal ai",
    "fal",
    "openai",
    "gemini",
  ];

  const regex = new RegExp(`\\b(${providers.join("|")})\\b`, "gi");

  return text.replace(regex, "provider");
}
